import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useApolloClient } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { GET_MY_CHAT, GET_MY_CHAT_UNREAD } from '../../graphql/queries/chat';
import { ASK_TIPPA, MARK_CHAT_READ, SEND_CHAT_MESSAGE } from '../../graphql/mutations/chat';
import { useBrand } from '../brand';
import { useViewer } from '../shell';
import { useTrack } from '../track';
import { useLiveRefresh } from '../lib/useLiveRefresh';
import { isPhone, useVisualViewportPin } from '../lib/useVisualViewportPin';
import {
  MAX_MULTI_PRICE, MIN_MULTI_PRICE, TIPPA_PROMPTS, TippaMulti, TippaQuaddie,
  type TippaChoice, type TippaMultiCard, type TippaQuaddieCard, type TippaRequest,
} from './TippaCards';

/** How often an open panel asks for new messages (only those after the newest it holds). */
export const OPEN_POLL_MS = 10_000;
export const MAX_MESSAGE_LENGTH = 2000;

export interface ChatMessage {
  id: number;
  fromTeam: boolean;
  body: string;
  createdAt: string;
  /** One of Tippa's automatic Ask Tippa answers. */
  fromTippa?: boolean;
  choices?: TippaChoice[] | null;
  multi?: TippaMultiCard | null;
  quaddie?: TippaQuaddieCard | null;
}

interface MemberChat {
  teamName: string;
  status: string | null;
  unreadCount: number;
  messages: ChatMessage[];
}

/** A message on screen: sent ones have a server id; ours in flight or failed have a local key. */
type Shown = ChatMessage & { key: string; state?: 'sending' | 'failed' };

const TIME = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' });
const DAY = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
const stamp = (iso: string) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString() ? TIME.format(d) : `${DAY.format(d)}, ${TIME.format(d)}`;
};

const shown = (m: ChatMessage): Shown => ({ ...m, key: `m${m.id}` });

/** A choice's request as the mutation takes it: without Apollo's __typename, and without the answers not given yet. */
const stripTypename = ({ topic, multi, meetingId, kind, combos, price, avoid }: TippaRequest): TippaRequest => {
  const out: TippaRequest = { topic };
  if (multi != null) out.multi = multi;
  if (meetingId != null) out.meetingId = meetingId;
  if (kind != null) out.kind = kind;
  if (combos != null) out.combos = combos;
  if (price != null) out.price = price;
  if (avoid != null) out.avoid = [...avoid];
  return out;
};

/** Which step of the flow a request answers, for analytics. */
const askStep = (r: TippaRequest) =>
  r.combos ? 'combos' : r.kind ? 'kind' : r.meetingId ? 'track' : r.avoid?.length ? 'spin' : r.price != null ? 'price' : r.multi ? 'multi' : 'start';

/**
 * Under the price buttons: the member's own price. Whole dollars, MIN_MULTI_PRICE to MAX_MULTI_PRICE.
 */
function OwnPrice({ disabled, onAsk }: { disabled: boolean; onAsk: (price: number) => void }) {
  const [value, setValue] = useState('');
  const price = Number(value);
  const valid = /^\d+$/.test(value) && price >= MIN_MULTI_PRICE && price <= MAX_MULTI_PRICE;
  return (
    <form
      className="ui-tippa-own"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onAsk(price);
      }}
    >
      <label htmlFor="ui-tippa-own-price">Or type your own price</label>
      <div>
        <span aria-hidden="true">$</span>
        <input
          id="ui-tippa-own-price"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          placeholder="25"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
          aria-describedby="ui-tippa-own-hint"
        />
        <button type="submit" disabled={disabled || !valid}>Build it</button>
      </div>
      <small id="ui-tippa-own-hint">${MIN_MULTI_PRICE} to ${MAX_MULTI_PRICE}</small>
    </form>
  );
}

/** Adds messages not already held (a poll can overlap a send), keeping server order. */
function merge(current: Shown[], incoming: ChatMessage[]): Shown[] {
  const held = new Set(current.filter((m) => !m.state).map((m) => m.id));
  const fresh = incoming.filter((m) => !held.has(m.id)).map(shown);
  if (!fresh.length) return current;
  const sent = current.filter((m) => !m.state);
  const pending = current.filter((m) => m.state);
  return [...sent, ...fresh].sort((a, b) => a.id - b.id).concat(pending);
}

export function ChatPanel({
  onClose,
  initialAsk = null,
  onInitialAsked,
}: {
  onClose: () => void;
  /** A multi or quaddie request made from outside the panel (the home page banner): asked as soon as the panel opens. */
  initialAsk?: TippaRequest | null;
  /** Called once `initialAsk` has been sent, so it isn't sent again. */
  onInitialAsked?: () => void;
}) {
  const client = useApolloClient();
  const brand = useBrand();
  const track = useTrack();
  // Multis and quaddies are for subscribers (the server checks too); everyone else gets feedback and questions.
  const canAsk = useViewer().isSubscribed;
  const { pathname } = useLocation();
  const [teamName, setTeamName] = useState<string | null>(null);
  const [messages, setMessages] = useState<Shown[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState('');
  const [sendError, setSendError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  /** The answer just tapped, shown selected while Tippa replies. */
  const [picked, setPicked] = useState<string | null>(null);
  const lastId = useRef(0);
  const localKey = useRef(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  useVisualViewportPin(panelRef, true);

  const markRead = useCallback(() => {
    void client
      .mutate({ mutation: MARK_CHAT_READ })
      .then(() => client.writeQuery({ query: GET_MY_CHAT_UNREAD, data: { myChatUnreadCount: 0 } }))
      .catch(() => undefined);
  }, [client]);

  const fetchMessages = useCallback(async () => {
    const { data } = await client.query<{ myChat: MemberChat }>({
      query: GET_MY_CHAT,
      variables: { afterId: lastId.current || null },
      fetchPolicy: 'network-only',
    });
    const chat = data.myChat;
    setTeamName(chat.teamName);
    setLoadError(false);
    if (chat.messages.length) {
      lastId.current = Math.max(lastId.current, ...chat.messages.map((m) => m.id));
      setMessages((current) => merge(current, chat.messages));
    }
    // Opening the panel, or a reply arriving while it is open, means it has been read.
    if (chat.unreadCount > 0) markRead();
  }, [client, markRead]);

  useEffect(() => {
    fetchMessages().catch(() => setLoadError(true));
    // Not on a phone: the keyboard would cover the conversation before it has been read.
    if (!isPhone()) inputRef.current?.focus();
  }, [fetchMessages]);

  useLiveRefresh(true, [fetchMessages], OPEN_POLL_MS);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length]);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const deliver = async (key: string, body: string) => {
    setSendError(null);
    setMessages((current) => current.map((m) => (m.key === key ? { ...m, state: 'sending' } : m)));
    try {
      const { data } = await client.mutate<{ sendChatMessage: ChatMessage }>({
        mutation: SEND_CHAT_MESSAGE,
        variables: { body, pagePath: pathname },
      });
      const saved = data!.sendChatMessage;
      lastId.current = Math.max(lastId.current, saved.id);
      setMessages((current) => {
        const rest = current.filter((m) => m.key !== key && m.id !== saved.id);
        return merge(rest, [saved]);
      });
      track('chat_message_sent', { length: body.length });
    } catch (err) {
      const message = (err as { graphQLErrors?: { message: string; extensions?: { code?: string } }[] }).graphQLErrors?.[0];
      // Our own checks (too long, too many) say what to do; anything else is a plain retry.
      setSendError(message?.extensions?.code === 'BAD_USER_INPUT' ? message.message : "Your message didn't send. Tap it to try again.");
      setMessages((current) => current.map((m) => (m.key === key ? { ...m, state: 'failed' } : m)));
    }
  };

  /** Ask Tippa: the member's tap and Tippa's answer come back together. */
  const ask = async (request: TippaRequest, label?: string) => {
    if (asking) return;
    setAsking(true);
    setPicked(label ?? null);
    setSendError(null);
    try {
      const { data } = await client.mutate<{ askTippa: ChatMessage[] }>({
        mutation: ASK_TIPPA,
        variables: { request: stripTypename(request), pagePath: pathname },
      });
      const saved = data!.askTippa;
      lastId.current = Math.max(lastId.current, ...saved.map((m) => m.id));
      setMessages((current) => merge(current, saved));
      track('ask_tippa', { topic: request.topic, step: askStep(request) });
    } catch (err) {
      const message = (err as { graphQLErrors?: { message: string; extensions?: { code?: string } }[] }).graphQLErrors?.[0];
      setSendError(message?.extensions?.code === 'BAD_USER_INPUT' ? message.message : "Sorry, we couldn't answer that just now. Please try again.");
    } finally {
      setAsking(false);
      setPicked(null);
    }
  };

  // A request from the home page banner: ask it once, as if the prompt had been tapped. A newly
  // opened panel's subscription check reads "not subscribed" until it loads, so wait for it rather
  // than drop the request (closing the panel clears a request that never gets asked).
  const askedRef = useRef<TippaRequest | null>(null);
  useEffect(() => {
    if (!initialAsk || !canAsk || askedRef.current === initialAsk) return;
    askedRef.current = initialAsk;
    onInitialAsked?.();
    void ask(initialAsk);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per request, once the member may ask
  }, [initialAsk, canAsk]);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || body.length > MAX_MESSAGE_LENGTH) return;
    localKey.current += 1;
    const key = `local${localKey.current}`;
    setMessages((current) => [...current, { key, id: 0, fromTeam: false, body, createdAt: new Date().toISOString(), state: 'sending' }]);
    setDraft('');
    void deliver(key, body);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const title = brand.chat?.title ?? teamName ?? 'Chat with us';
  const intro = brand.chat?.intro ?? 'Questions, ideas, something not right? We read every message.';
  const tooLong = draft.trim().length > MAX_MESSAGE_LENGTH;
  const lastKey = messages[messages.length - 1]?.key;

  return (
    <section ref={panelRef} className="ui-chat-panel" role="dialog" aria-label={title}>
      <header className="ui-chat-head">
        <div>
          <h2>{title}</h2>
          <p>{intro}</p>
        </div>
        <button type="button" className="ui-chat-x" aria-label="Close chat" onClick={onClose}>×</button>
      </header>

      <div className="ui-chat-log" aria-live="polite">
        {loadError && !messages.length && <p className="ui-chat-note">We couldn't load your messages. We'll keep trying.</p>}
        {!loadError && !messages.length && (
          <p className="ui-chat-note">
            Send us a message and {teamName ?? 'the team'} will reply here. We'll email you if you're away.
            {canAsk && ' Or tap below for a multi or a quaddie, straight away.'}
          </p>
        )}
        <ol>
          {messages.map((m) => (
            <li key={m.key} className={m.fromTeam ? 'team' : 'me'} data-state={m.state}>
              {m.fromTeam && <span className="who">{teamName}</span>}
              {m.state === 'failed' ? (
                <button type="button" className="bubble" onClick={() => void deliver(m.key, m.body)} aria-label={`Retry sending: ${m.body}`}>
                  {m.body}
                </button>
              ) : (
                <div className="bubble">{m.body}</div>
              )}
              {m.multi && <TippaMulti card={m.multi} />}
              {m.quaddie && <TippaQuaddie card={m.quaddie} />}
              {/* Only the latest question's buttons work: an earlier one has been answered. */}
              {canAsk && m.choices && m.choices.length > 0 && m.key === lastKey && (
                <>
                <div className="ui-tippa-choices" role="group" aria-label="Your answer">
                  {m.choices.map((c) => (
                    <button type="button" key={c.label} disabled={asking} aria-pressed={picked === c.label} onClick={() => void ask(c.request, c.label)}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {/* the price question: the member may name their own */}
                {m.choices.some((c) => c.request.price != null && !c.request.avoid?.length) && (
                  <OwnPrice disabled={asking} onAsk={(price) => void ask({ topic: 'MULTI', price }, `About $${price}`)} />
                )}
                </>
              )}
              <span className="when">
                {m.state === 'sending' ? 'Sending…' : m.state === 'failed' ? 'Not sent · tap to retry' : stamp(m.createdAt)}
              </span>
            </li>
          ))}
        </ol>
        <div ref={endRef} />
      </div>

      <form className="ui-chat-form" onSubmit={submit}>
        {sendError && <p className="ui-chat-err" role="alert">{sendError}</p>}
        {canAsk && (
          <div className="ui-tippa-prompts" role="group" aria-label={`Ask ${teamName ?? 'us'}`}>
            {TIPPA_PROMPTS.map((p) => (
              <button type="button" key={p.label} disabled={asking} onClick={() => void ask(p.request)}>{p.label}</button>
            ))}
          </div>
        )}
        <label htmlFor="ui-chat-input" className="ui-chat-sr">Your message</label>
        <textarea
          id="ui-chat-input"
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder="Type a message…"
        />
        <div className="ui-chat-actions">
          {tooLong && <span className="ui-chat-err">Up to {MAX_MESSAGE_LENGTH} characters</span>}
          <button type="submit" disabled={!draft.trim() || tooLong}>Send</button>
        </div>
      </form>
    </section>
  );
}
